import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { BullhornAuthData, fetchBullhornJobOrdersRaw, fetchBullhornJobOrderFiles, fetchBullhornJobSubmissions } from '../services/bullhornService';
import { parseBullhornRawToVacancy } from '../services/geminiService';
import { supabase_sync_bullhorn_vacancies, supabase_sync_bullhorn_vacancy_files, supabase_sync_bullhorn_job_submissions, supabase_get_bullhorn_clients, supabase } from '../services/supabaseService';
import { Loader2, Sparkles, Save, ChevronRight, ChevronLeft, X, Database, CheckCircle2, RefreshCw, Briefcase, FileText } from 'lucide-react';

interface Props {
  user: User;
  bhAuth: BullhornAuthData;
  onClose: () => void;
}

export const BullhornInteractiveVacancyMapper: React.FC<Props> = ({ user, bhAuth, onClose }) => {
  const [vacancies, setVacancies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mappedProfile, setMappedProfile] = useState<any | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle'|'saving'|'success'|'error'>('idle');
  const [saveProgressText, setSaveProgressText] = useState('');

  // Extra raw data fetch states for the left side
  const [currentVacancyFiles, setCurrentVacancyFiles] = useState<any[]>([]);
  const [currentVacancySubmissions, setCurrentVacancySubmissions] = useState<any[]>([]);
  const [isFetchingExtras, setIsFetchingExtras] = useState(false);
  
  // Auto Loop Logic
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  const [mappedClients, setMappedClients] = useState<any[]>([]);

  // Init CRM clients
  useEffect(() => {
    supabase_get_bullhorn_clients(user.clientId as string).then(setMappedClients);
  }, [user.clientId]);

  // Load all open unmapped vacancies
  useEffect(() => {
    const loadVacancies = async () => {
      setIsLoading(true);
      setLoadingMessage('Fetching vacancies...');
      try {
        const query = 'isDeleted:0 AND isOpen:1'; // Active open vacancies
        // Use fetchAll = true and pass a progress callback
        const res = await fetchBullhornJobOrdersRaw(bhAuth, query, true, true, (msg) => {
            setLoadingMessage(msg);
        });
        setVacancies(res.data || []);
      } catch (err) {
        console.error("Failed to load vacancies:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadVacancies();
  }, [bhAuth]);

  const currentVacancy = vacancies[currentIndex];

  useEffect(() => {
     const fetchExtras = async () => {
        if (!currentVacancy) return;
        setIsFetchingExtras(true);
        try {
            const files = await fetchBullhornJobOrderFiles(bhAuth, currentVacancy.id);
            setCurrentVacancyFiles(files || []);
            
            const submissions = await fetchBullhornJobSubmissions(bhAuth, currentVacancy.id);
            setCurrentVacancySubmissions(submissions || []);
        } catch(e) {
            console.error("Failed fetching extra vacancy info:", e);
        } finally {
            setIsFetchingExtras(false);
        }
     };
     fetchExtras();
  }, [currentIndex, vacancies.length, currentVacancy, bhAuth]);

  const handleSyncMissing = async () => {
      const confirmSync = window.confirm("Dit zal alle ontbrekende vacatures vanuit Bullhorn ophalen en als 'Archief' toevoegen (bestaande blijven onaangetast). Dit kan even duren. Doorgaan?");
      if (!confirmSync) return;

      setIsLoading(true);
      setLoadingMessage('Haalt alle vacatures op uit Bullhorn (ook gesloten)...');
      try {
          if (!mappedClients || mappedClients.length === 0) {
              alert('Geen CRM bedrijven gevonden. Wacht even of synchroniseer eerst Bedrijven!');
              return;
          }
          const res = await fetchBullhornJobOrdersRaw(bhAuth, 'isDeleted:0', true, true, (msg) => setLoadingMessage(msg));
          const allBhVacs = res.data || [];
          
          setLoadingMessage('Vergelijken met lokale database...');
          
          // Get all existing bullhorn_ids (chunked to avoid PostgREST limits)
          const existingIds = new Set<string>();
          let from = 0;
          let hasMore = true;
          while (hasMore) {
              const { data } = await supabase.from('vacancies').select('bullhorn_id').range(from, from + 999);
              if (data && data.length > 0) {
                  data.forEach(v => existingIds.add(String(v.bullhorn_id)));
                  from += 1000;
                  if (data.length < 1000) hasMore = false;
              } else {
                  hasMore = false;
              }
          }

          const missingVacs = allBhVacs.filter((v: any) => !existingIds.has(String(v.id)));
          
          if (missingVacs.length === 0) {
              alert("Geen ontbrekende vacatures gevonden.");
              setIsLoading(false);
              return;
          }

          setLoadingMessage(`${missingVacs.length} ontbrekende vacatures gevonden. Bezig met voorbereiden...`);
          
          const safePayloads = missingVacs.map((v: any) => {
              const p = { ...v, status: 'Archive' };
              return JSON.parse(JSON.stringify(p).replace(/\\u0000/g, ''));
          });
          
          const vacRes = await supabase_sync_bullhorn_vacancies(user.clientId as string, safePayloads, mappedClients, (msg) => setLoadingMessage(msg));
          
          if (!vacRes.success) {
              alert("Fout bij opslaan: " + vacRes.error);
          } else {
              const insertedBhIds = safePayloads.map((v: any) => String(v.id));
              setLoadingMessage('Status forceren naar Archief...');
              
              for (let i = 0; i < insertedBhIds.length; i += 100) {
                  const chunk = insertedBhIds.slice(i, i + 100);
                  const { data: newlyInserted } = await supabase.from('vacancies').select('id, data').in('bullhorn_id', chunk);
                  
                  if (newlyInserted) {
                      await Promise.all(newlyInserted.map(row => {
                          const updatedData = { ...row.data, lifecycleStatus: 'Archief', isClosed: true };
                          return supabase.from('vacancies').update({ status: 'Archief', data: updatedData }).eq('id', row.id);
                      }));
                  }
              }
              
              alert(`Succesvol ${missingVacs.length} ontbrekende vacatures toegevoegd als Archief!`);
          }
      } catch (err) {
          console.error(err);
          alert("Er is een fout opgetreden bij het synchroniseren van ontbrekende vacatures.");
      } finally {
          setIsLoading(false);
      }
  };

  const handleNext = () => {
    if (currentIndex < vacancies.length - 1) {
       setCurrentIndex(currentIndex + 1);
       setMappedProfile(null);
       setSaveStatus('idle');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
       setCurrentIndex(currentIndex - 1);
       setMappedProfile(null);
       setSaveStatus('idle');
    }
  };

  const handleAnalyze = async () => {
    if (!currentVacancy) return;
    setIsAnalyzing(true);
    setSaveStatus('idle');
    try {
      // OVERRIDE: Gebruiker wil geen AI meer tijdens de import, moet puur RAW Bullhorn zijn!
      const parsed = {
         title: currentVacancy.title,
         category: currentVacancy.employmentType || 'Algemeen',
         summary: '',
         keywords: []
      };
      setMappedProfile({ ...parsed });
    } catch (err) {
      console.error(err);
      setIsAutoMode(false);
      alert("AI Analyseren mislukt. Probeer het opnieuw.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveAndSync = async () => {
    if (!currentVacancy) return;
    setIsSaving(true);
    setSaveStatus('saving');
    setSaveProgressText('Vacature opslaan in database...');

    try {
      if (!mappedClients || mappedClients.length === 0) {
          throw new Error('Geen CRM bedrijven gevonden. Synchroniseer eerst Bedrijven!');
      }

      // 1. We sturen een aangepaste Vacancy structuur door naar de bulk sync logic, 
      // of we doen het handmatig. Aangezien we AI overrides hebben, laten we de standaard functie gebruiken,
      // maar we proppen de AI text over de raw heen. Wait, let's inject it into the data object!
      
      const payload = { ...currentVacancy };
      if (mappedProfile) {
          // Overschrijf de titel en injecteer de summary etc
          payload.title = mappedProfile.title || currentVacancy.title;
          // Restore original description. De gebruiker wil deze in de Intake Bewaren zodat ie daar zichtbaar is!
          payload.description = currentVacancy.publicDescription || currentVacancy.description;
          payload.custom_ai_summary = mappedProfile.summary;
          payload.custom_ai_keywords = mappedProfile.keywords;
          payload.custom_ai_category = mappedProfile.category;
      }
      
      // Strip null bytes from payload! Postgres JSONB will crash with 400 Bad Request if it encounters \u0000
      const safePayload = JSON.parse(JSON.stringify(payload).replace(/\\u0000/g, ''));
      
      const vacRes = await supabase_sync_bullhorn_vacancies(user.clientId as string, [safePayload], mappedClients, (msg) => setSaveProgressText(msg));
      
      if (!vacRes.success) {
          throw new Error(vacRes.error || 'Fout bij bulk sync vacatures');
      }
      
      const { data: localVacs } = await supabase.from('vacancies').select('id').eq('bullhorn_id', currentVacancy.id.toString());
      const localVacId = localVacs?.[0]?.id;
      
      if (!localVacId) {
          throw new Error('Vacature lokaal niet gevonden na insert.');
      }

      // Upsert de AI overrides netjes in de data structuur (omdat de standaard sync het ms in een fixed veld zet)
      if (mappedProfile) {
          const safeMappedProfile = JSON.parse(JSON.stringify(mappedProfile).replace(/\\u0000/g, ''));
          const { data: latestVac } = await supabase.from('vacancies').select('data').eq('id', localVacId).single();
          const newData = {
              ...(latestVac?.data || {}),
              intro_text: safeMappedProfile.summary,
              keywords: safeMappedProfile.keywords,
              category: safeMappedProfile.category,
              builtVacancySections: safeMappedProfile.builtVacancySections
          };
          await supabase.from('vacancies').update({ data: newData }).eq('id', localVacId);
      }

      // Deep Sync Files
      if (currentVacancyFiles.length > 0) {
          setSaveProgressText(`Koppelen van ${currentVacancyFiles.length} bestanden...`);
          await supabase_sync_bullhorn_vacancy_files(localVacId, currentVacancyFiles);
      }

      // Deep Sync Job Submissions (Vacature Pipelines)
      if (currentVacancySubmissions.length > 0) {
          setSaveProgressText(`Aanmaken en koppelen van ${currentVacancySubmissions.length} sollicitaties/kandidaten...`);
          const subRes = await supabase_sync_bullhorn_job_submissions(user.clientId as string, localVacId, currentVacancySubmissions);
          if (!subRes.success) throw new Error(subRes.error || 'Fout bij syncen sollicitaties');
      }

      setSaveProgressText('Succes!');
      setSaveStatus('success');
      
      setTimeout(() => {
          handleNext();
      }, 1500);

    } catch (err: any) {
       console.error("Save failed:", err);
       setSaveStatus('error');
       setIsAutoMode(false); // Pauzeer bij error
       setSaveProgressText('Fout: ' + (err.message || 'Onbekend'));
    } finally {
       setIsSaving(false);
    }
  };

  // State Machine for Auto-Loop
  useEffect(() => {
     if (!isAutoMode) return;
     if (isFetchingExtras) return; // Wait for initial raw data fetch

     if (!mappedProfile && !isAnalyzing && saveStatus === 'idle') {
         handleAnalyze();
         return;
     }

     if (mappedProfile && saveStatus === 'idle' && !isSaving) {
         handleSaveAndSync();
         return;
     }
  }, [isAutoMode, isFetchingExtras, mappedProfile, isAnalyzing, saveStatus, isSaving]);

  return (
    <div className="fixed inset-0 bg-slate-900 z-[60] flex flex-col font-sans">
      <div className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between shrink-0 shadow-lg text-white">
        <h1 className="text-xl font-black tracking-tight flex items-center gap-3">
            <Briefcase size={24} className="text-[#e51b23]" />
            Vacancy Interactive Mapper <span className="text-sm font-bold text-slate-500 uppercase tracking-widest ml-4">Bullhorn Sync Phase 2</span>
        </h1>
        <div className="flex items-center gap-4">
            <button 
              onClick={handleSyncMissing}
              disabled={isLoading || isSaving}
              className="px-4 py-2 bg-slate-800 text-slate-300 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-50"
            >
              Sync Ontbrekende (Archief)
            </button>
            <button 
              onClick={onClose}
              className="w-10 h-10 bg-slate-800 text-slate-400 rounded-full flex items-center justify-center hover:bg-slate-700 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-4">
           <Loader2 size={40} className="animate-spin text-[#e51b23]" />
           <p className="text-sm font-bold uppercase tracking-widest animate-pulse">{loadingMessage || 'Loading Vacancies from Bullhorn...'}</p>
        </div>
      ) : vacancies.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-500">
           No open vacancies available for sync.
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
           {/* PROGRESS BAR */}
           <div className="h-1 bg-slate-800 w-full">
               <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${((currentIndex) / Math.max(1, vacancies.length)) * 100}%` }}></div>
           </div>
           
           <div className="bg-slate-800 px-6 py-3 flex items-center justify-between border-b border-slate-700 shadow-md">
               <div className="flex items-center gap-4">
                   <button onClick={handlePrev} disabled={currentIndex === 0 || isSaving || isAutoMode} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-xs font-bold uppercase disabled:opacity-50 transition-colors">
                      <ChevronLeft size={16} /> Vorige Vacature
                   </button>
                   
                   <button 
                      onClick={() => setIsAutoMode(!isAutoMode)}
                      className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg transition-transform hover:scale-105 active:scale-95 ${
                         isAutoMode ? 'bg-amber-500 text-amber-950 hover:bg-amber-400' : 'bg-[#e51b23] text-white hover:bg-red-600'
                      }`}
                   >
                      {isAutoMode ? <span className="flex items-center gap-2"><div className="w-2 h-2 bg-amber-950 rounded-full animate-pulse"></div> Pauzeer Auto-Mappen</span> : <span className="flex items-center gap-2"><Sparkles size={14} /> Start Auto-Mappen</span>}
                   </button>
               </div>
               
               <div className="flex items-center gap-4 text-xs font-black text-slate-400 tracking-widest uppercase">
                  <span>Vacature {currentIndex + 1} van {vacancies.length}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span>
                  <span className="text-emerald-400">BH JobOrder: #{currentVacancy.id}</span>
               </div>
               
               <button onClick={handleNext} disabled={currentIndex === vacancies.length - 1 || isSaving || isAutoMode} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-xs font-bold uppercase disabled:opacity-50 transition-colors">
                  Volgende Vacature <ChevronRight size={16} />
               </button>
           </div>

           <div className="flex-1 flex flex-col lg:flex-row overflow-hidden bg-slate-100">
               {/* LEFT SIDE: RAW BULLHORN */}
               <div className="flex-1 flex flex-col border-r border-slate-300 relative bg-white">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-2 text-slate-700 font-black text-sm uppercase tracking-widest">
                          <Database size={16} className="text-indigo-500" /> RAW Bullhorn Vacature
                      </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                      <div className="space-y-1">
                          <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Job Titel & Details</div>
                          <div className="text-2xl font-black text-slate-900 leading-none">{currentVacancy.title}</div>
                          <div className="text-sm font-medium text-slate-600 flex flex-col gap-1 pt-2">
                             <span>🏢 Bedrijf: {currentVacancy.clientCorporation?.name || 'Onbekend'}</span>
                             <span>📍 Locatie: {currentVacancy.address?.city || 'Onbekend'}</span>
                             <span>⏳ Uren: {currentVacancy.employmentType || currentVacancy.durationWeeks ? `${currentVacancy.durationWeeks}weken` : 'Onbekend'}</span>
                             <span>💶 Salaris: {currentVacancy.salary ? `${currentVacancy.salaryUnit}: ${currentVacancy.salary}` : 'Niet Gepubliceerd'}</span>
                             <span>🚦 Status: {currentVacancy.status}</span>
                          </div>
                      </div>

                      {currentVacancy.description && (
                         <div className="space-y-2">
                             <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Raw Description / Vacaturetekst</div>
                             <div className="bg-yellow-50/50 border border-yellow-100 p-3 rounded-lg text-[11px] text-slate-700 font-mono overflow-hidden max-h-40 overflow-y-auto custom-scrollbar">
                                 {currentVacancy.description.replace(/<[^>]+>/g, '')}
                             </div>
                         </div>
                      )}

                      {/* EXTRA FETCHED ITEMS */}
                      {isFetchingExtras ? (
                         <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
                      ) : (
                         <>
                            {currentVacancyFiles.length > 0 && (
                               <div className="space-y-2">
                                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Gevonden Documenten ({currentVacancyFiles.length})</div>
                                  <div className="flex flex-wrap gap-2">
                                     {currentVacancyFiles.map((f:any, i:number) => (
                                        <div key={i} className="bg-slate-100 px-3 py-2 rounded-lg text-xs font-medium text-slate-700 border border-slate-200 flex items-center gap-2">
                                           <FileText size={14} className="text-slate-400" /> {f.name || f.fileType || 'Document'}
                                        </div>
                                     ))}
                                  </div>
                               </div>
                            )}

                            {currentVacancySubmissions.length > 0 && (
                               <div className="space-y-2">
                                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Sollicitaties / Submissions ({currentVacancySubmissions.length})</div>
                                  <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                                     {currentVacancySubmissions.map((s:any, i:number) => (
                                        <div key={i} className="bg-emerald-50/50 border border-emerald-100 p-3 rounded-lg flex justify-between items-center">
                                           <div>
                                              <div className="text-xs font-bold text-slate-900">{s.candidate?.firstName} {s.candidate?.lastName} (ID: {s.candidate?.id})</div>
                                              <div className="text-[10px] text-slate-500 mt-1">Status: {s.status} | Toegevoegd op: {new Date(s.dateAdded).toLocaleDateString()}</div>
                                           </div>
                                        </div>
                                     ))}
                                  </div>
                               </div>
                            )}
                         </>
                      )}
                  </div>
               </div>

               {/* MIDDLE: THE BUTTON */}
               <div className="lg:w-20 bg-slate-200 border-x border-slate-300 flex flex-row lg:flex-col items-center justify-center p-4 relative z-10">
                   <button 
                     onClick={handleAnalyze} 
                     disabled={isAnalyzing || isSaving}
                     className="bg-[#e51b23] hover:bg-black text-white w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center transform transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 disabled:bg-slate-400 group"
                     title="Analyseer en Formatteer met Froukje AI"
                   >
                      {isAnalyzing ? <Loader2 size={24} className="animate-spin" /> : <Sparkles size={24} className="group-hover:animate-pulse" />}
                   </button>
                   <div className="hidden lg:block mt-6 text-[#e51b23] font-black text-[10px] tracking-widest uppercase rotate-180" style={{ writingMode: 'vertical-rl' }}>
                       Froukje Bridge
                   </div>
               </div>

               {/* RIGHT SIDE: MAPPED SUPABASE DATA */}
               <div className="flex-1 flex flex-col relative bg-slate-50">
                  <div className="p-4 bg-white border-b border-slate-200 flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-2 text-emerald-600 font-black text-sm uppercase tracking-widest">
                          <CheckCircle2 size={16} /> Werftools Database Profiel
                      </div>
                      
                      {mappedProfile && (
                         <button 
                            onClick={handleSaveAndSync} 
                            disabled={isSaving || saveStatus === 'success'}
                            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-md transition-all ${
                               saveStatus === 'success' ? 'bg-emerald-500 text-white' : 
                               saveStatus === 'error' ? 'bg-red-500 text-white' : 
                               'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                         >
                             {isSaving ? <Loader2 size={16} className="animate-spin" /> : saveStatus === 'success' ? <CheckCircle2 size={16} /> : <Save size={16} />}
                             <div className="flex flex-col items-start border-l border-white/20 pl-3 leading-tight">
                                <span>{isSaving ? 'Bezig met opslaan...' : saveStatus === 'success' ? 'Voltooid' : 'Opslaan & Pipeline Syncen'}</span>
                                {isSaving && saveProgressText && <span className="text-[9px] text-emerald-100 uppercase tracking-widest mt-0.5">{saveProgressText}</span>}
                             </div>
                         </button>
                      )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                      {!mappedProfile ? (
                         <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                             <Briefcase size={48} className="text-slate-300 mb-4" />
                             <h3 className="text-lg font-black text-slate-800">Geen AI output gemaakt</h3>
                             <p className="text-sm text-slate-500 max-w-sm mt-2">Druk op de Froukje AI knop in het midden om de linker vacatureteksten te structureren voor SEO en helderheid in Werftools.</p>
                         </div>
                      ) : (
                         <div className="animate-fadeIn space-y-6">
                             {/* CLEAN HERO RECORD */}
                             <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 flex items-start justify-between">
                                 <div>
                                     <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                                         {mappedProfile.title || currentVacancy.title}
                                         <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-1 rounded font-bold uppercase tracking-widest">{mappedProfile.category || 'Algemeen'}</span>
                                     </h2>
                                     <div className="text-sm font-bold text-slate-600 mt-1">{currentVacancy.clientCorporation?.name || 'Onbekend Bedrijf'}</div>
                                     <p className="text-sm text-slate-500 leading-relaxed mt-4 max-w-xl italic border-l-4 border-slate-200 pl-4">
                                        "{mappedProfile.summary}"
                                     </p>
                                 </div>
                             </div>

                             {/* CLEAN KEYWORDS */}
                             {mappedProfile.keywords && mappedProfile.keywords.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Uitgelichte Eisen (Keywords)</h3>
                                     <div className="flex flex-wrap gap-2">
                                         {mappedProfile.keywords.map((s: string, i: number) => (
                                            <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold">{s}</span>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* STRUCTURED SECTIONS */}
                             {mappedProfile.builtVacancySections && mappedProfile.builtVacancySections.length > 0 ? (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Gestructureerde Secties (Verbatim)</h3>
                                     <div className="space-y-4 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                                         {mappedProfile.builtVacancySections.map((sec: any, i: number) => (
                                             <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                                                 <div className="text-xs font-bold text-slate-800 mb-1">{sec.title}</div>
                                                 <div className="text-[11px] text-slate-600 font-mono leading-relaxed whitespace-pre-wrap">{sec.content || '- LEEG -'}</div>
                                             </div>
                                         ))}
                                     </div>
                                 </div>
                             ) : mappedProfile.description && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Volledige Vacaturetekst (Verbatim)</h3>
                                     <div className="text-[11px] text-slate-700 font-mono overflow-y-auto max-h-40 custom-scrollbar">
                                         {mappedProfile.description}
                                     </div>
                                 </div>
                             )}

                             {/* DEEP SYNC TRACKER */}
                             <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                 <h3 className="text-[10px] uppercase font-black tracking-widest text-emerald-500 mb-4 flex items-center gap-2">
                                     <RefreshCw size={12} className="animate-spin-slow" /> Wordt Diep-Gesynchroniseerd en Gekoppeld in Pipeline:
                                 </h3>
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                     <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                                         <div className="text-2xl font-black text-slate-800">{currentVacancyFiles.length}</div>
                                         <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Vacature Bijlagen</div>
                                     </div>
                                     <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                                         <div className="text-2xl font-black text-slate-800">{currentVacancySubmissions.length}</div>
                                         <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Actieve Kandidaten Pipeline</div>
                                     </div>
                                 </div>
                             </div>
                         </div>
                      )}
                  </div>
               </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default BullhornInteractiveVacancyMapper;
